import {
  describeConcentration,
  describeRuleDose,
  formatRange,
  type DoseCalculation,
  type DoseRule,
  type Medication,
} from '@ems/clinical';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { Badge } from './ui';

const POP_LABEL: Record<string, string> = { adult: 'Adult', pediatric: 'Pediatric', neonate: 'Neonate', all: 'All ages' };

/**
 * Renders a calculated dose. Every result shows the arithmetic behind it so the provider (and their
 * partner) can verify the number rather than trust it.
 */
export function DoseCard({
  medication,
  rule,
  calculation,
  showMedication = false,
  muted = false,
}: {
  medication: Medication & { slug?: string };
  rule: DoseRule;
  calculation: DoseCalculation;
  showMedication?: boolean;
  muted?: boolean;
}) {
  const { status, dose, quantity, rateMlPerHr, concentration, capped } = calculation;
  const quantityUnit = quantity?.unit === 'mL' ? 'mL' : `${quantity?.unit}${quantity && (quantity.high ?? quantity.low) !== 1 ? 's' : ''}`;

  return (
    <article
      className={cn(
        'rounded-md border bg-white p-3.5 dark:bg-ink-900',
        capped ? 'border-amber-500 dark:border-amber-400/60' : 'border-stone-200 dark:border-stone-800',
        muted && 'opacity-70',
      )}
    >
      <header className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          {showMedication && (
            <Link to={`/meds/${medication.slug ?? medication.id}`} className="flex items-center gap-1.5 font-semibold text-stone-950 underline-offset-[3px] hover:underline dark:text-white">
              {medication.highAlert && <ShieldAlert className="size-4 text-red-600" aria-label="High-alert medication" />}
              {medication.genericName}
            </Link>
          )}
          <div className={cn(showMedication ? 'text-sm text-stone-600 dark:text-stone-300' : 'font-semibold')}>{rule.indication}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Badge tone="neutral">{POP_LABEL[rule.population]}</Badge>
          {rule.routes.map((r) => (
            <Badge key={r} tone="brand">
              {r}
            </Badge>
          ))}
        </div>
      </header>

      {status === 'not-applicable' ? (
        <p className="mt-2 text-sm text-stone-500">
          {describeRuleDose(rule)} — {calculation.issues.join('; ')}
        </p>
      ) : status === 'needs-weight' ? (
        <div className="mt-2 text-sm">
          <div className="font-medium">{describeRuleDose(rule)}</div>
          <div className="text-stone-500">Enter patient weight to calculate.</div>
        </div>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {dose && (
              <span className="text-2xl font-semibold tabular-nums tracking-tight">
                {formatRange(dose.low, dose.high)} <span className="text-lg font-semibold">{dose.unit}{rule.kind === 'infusion' ? `/${rule.rateBasis ?? 'min'}` : ''}</span>
              </span>
            )}
            {quantity && (
              <span className="rounded-[4px] bg-stone-900 px-2 py-0.5 text-lg font-semibold tabular-nums text-white dark:bg-stone-100 dark:text-stone-900">
                = {formatRange(quantity.low, quantity.high)} {quantityUnit}
              </span>
            )}
            {rateMlPerHr && (
              <span className="rounded-[4px] bg-stone-900 px-2 py-0.5 text-lg font-semibold tabular-nums text-white dark:bg-stone-100 dark:text-stone-900">
                = {formatRange(rateMlPerHr.low, rateMlPerHr.high)} mL/hr
              </span>
            )}
          </div>
          {concentration && <div className="text-sm text-stone-500 dark:text-stone-400">using {describeConcentration(concentration)}</div>}
          {rule.kind !== 'infusion' && (rule.kind === 'weight' || rule.maxSingle !== undefined) && <div className="text-xs text-stone-500">{describeRuleDose(rule)}</div>}

          {calculation.warnings.map((w) => (
            <p key={w} className="mt-2 flex items-start gap-1.5 border-l-2 border-amber-500 bg-amber-50 py-1 pl-2 pr-2 text-sm font-medium text-amber-900 dark:border-amber-400 dark:bg-amber-400/10 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" /> {w}
            </p>
          ))}

          <ol className="mt-2.5 space-y-0.5 border-l-2 border-stone-200 pl-2.5 font-mono text-xs leading-relaxed text-stone-600 dark:border-stone-700 dark:text-stone-400" aria-label="Calculation steps">
            {calculation.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </>
      )}

      {(rule.administration || rule.notes || calculation.info.length > 0) && status !== 'not-applicable' && (
        <div className="mt-2 space-y-1 text-sm text-stone-600 dark:text-stone-300">
          {rule.administration && <p>{rule.administration}</p>}
          {rule.notes && <p className="text-stone-500 dark:text-stone-400">{rule.notes}</p>}
          {calculation.info
            .filter((i) => !i.startsWith('No maximum'))
            .map((i) => (
              <p key={i} className="text-xs text-stone-500">
                {i}
              </p>
            ))}
        </div>
      )}
    </article>
  );
}
